import React, {CSSProperties, useState} from 'react';
import styled from 'styled-components';
import {useApiContext} from '../../api';
import {ItemDetailsDialog} from '../../common/dialogs/ItemDetailsDialog';
import {
  buildTargetItemPropsMap,
  TargetItemProps,
} from '../../fields/hooks/useItemProps';
import {Actions} from '../../types/Action';
import {ResourceDetails} from '../../types/ResourceDetails';
import {Item} from './Item';
import {RelatedSchemas} from '../../types/Resource';

export type ItemRenderer = (
  item: ResourceDetails,
  index: number,
) => JSX.Element | null;

type Props = {
  targets: TargetItemProps[];
  items: ResourceDetails[];
  relatedSchemas?: RelatedSchemas;
  actions: Actions;
  itemRenderer?: ItemRenderer;
  renderAsPlaneText?: boolean;
  styles?: {
    item?: CSSProperties;
  };
};

export function Items(props: Props): JSX.Element | null {
  const [itemId, setItemId] = useState<string>();
  const [schemaId, setSchemaId] = useState<string>();
  const [shown, setShown] = useState<boolean>(false);
  const ctx = useApiContext();

  if (props.items.length === 0) {
    return null;
  }

  const targetMap = buildTargetItemPropsMap(props.targets);

  const renderer: ItemRenderer =
    props.itemRenderer ||
    function (item: ResourceDetails, index: number) {
      const schemaId = item.schema_id;
      const target = targetMap[schemaId];
      const relatedSchema = props.relatedSchemas?.[schemaId];

      return (
        <ItemWrapper
          {...props}
          key={`item-${index}`}
          item={item}
          onClick={async () => {
            await setItemId(item.id);

            if (schemaId) {
              setSchemaId(schemaId);
            }

            setShown(true);
          }}
          style={props.styles?.item}
          nameFieldId={target?.nameFieldId}
          iconColor={target?.itemSchema.iconColor || relatedSchema?.iconColor}
        />
      );
    };

  return (
    <Container>
      {props.items.map(renderer)}
      <ItemDetailsDialog
        ctx={ctx}
        shown={shown}
        schemaId={schemaId}
        resId={itemId}
        onClose={() => {
          setShown(false);
        }}
        actions={props.actions}
        minWidth={'400px'}
      />
    </Container>
  );
}

type ItemProps = {
  item: ResourceDetails;
  nameFieldId: string;
  iconColor?: string;
  renderAsPlaneText?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
};

function ItemWrapper(props: ItemProps): JSX.Element {
  if (props.renderAsPlaneText) {
    return (
      <PlaneTextItemContainer style={props.style}>
        <Item {...props} />
      </PlaneTextItemContainer>
    );
  }

  return (
    <ItemContainer style={props.style} onClick={props.onClick}>
      <Item {...props} />
    </ItemContainer>
  );
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ItemContainer = styled.div`
  max-width: 100%;
  cursor: pointer;
`;

const PlaneTextItemContainer = styled.div`
  max-width: 100%;
`;
